import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { getContactProfile, getContactProfiles } from '../lib/api';
import {
  Bot,
  Building2,
  ContactRound,
  Link2,
  Mail,
  MessageSquareText,
  Phone,
  RefreshCw,
  Sparkles,
  UserRound,
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';

const fieldRows = [
  { key: 'name', label: 'Name', icon: UserRound },
  { key: 'email', label: 'Email', icon: Mail },
  { key: 'phone', label: 'Phone', icon: Phone },
  { key: 'company', label: 'Company', icon: Building2 },
];

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const roleMeta = {
  user: {
    label: 'Contact',
    icon: UserRound,
    bubble: 'bg-secondary text-foreground',
    wrapper: 'justify-end',
  },
  assistant: {
    label: 'Apollo',
    icon: Bot,
    bubble: 'bg-[#0047FF] text-white',
    wrapper: 'justify-start',
  },
  system: {
    label: 'System',
    icon: Sparkles,
    bubble: 'bg-muted text-foreground',
    wrapper: 'justify-start',
  },
};

const ContactsPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getContactProfiles();
      setProfiles(data);
      if (data.length > 0) {
        const nextId = selectedId && data.some((profile) => profile.id === selectedId) ? selectedId : data[0].id;
        setSelectedId(nextId);
      } else {
        setSelectedId(null);
        setSelectedProfile(null);
      }
    } catch (error) {
      console.error('Failed to fetch contact profiles:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (!selectedId) return;

    const fetchProfile = async () => {
      setDetailLoading(true);
      try {
        const { data } = await getContactProfile(selectedId);
        setSelectedProfile(data);
      } catch (error) {
        console.error('Failed to fetch contact profile:', error);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchProfile();
  }, [selectedId]);

  const totalWithEmail = useMemo(() => profiles.filter((profile) => Boolean(profile.email)).length, [profiles]);
  const totalWithPhone = useMemo(() => profiles.filter((profile) => Boolean(profile.phone)).length, [profiles]);

  return (
    <Layout>
      <div className="p-8 h-screen flex flex-col gap-6" data-testid="contacts-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight font-['Outfit']">Contacts</h1>
            <p className="text-muted-foreground mt-1">Captured lead details from your Apollo conversations, stored locally.</p>
          </div>
          <Button variant="outline" className="rounded-lg gap-2" onClick={fetchProfiles} data-testid="refresh-contacts-button">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="apollo-card p-5">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">Captured contacts</p>
            <p className="text-3xl font-semibold mt-2 font-['Outfit']">{profiles.length}</p>
          </div>
          <div className="apollo-card p-5">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">With email</p>
            <p className="text-3xl font-semibold mt-2 font-['Outfit']">{totalWithEmail}</p>
          </div>
          <div className="apollo-card p-5">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">With phone</p>
            <p className="text-3xl font-semibold mt-2 font-['Outfit']">{totalWithPhone}</p>
          </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-6">
          <div className="apollo-card min-h-0 flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold font-['Outfit']">Lead Inbox</h2>
              <p className="text-sm text-muted-foreground mt-1">Newest captured details first</p>
            </div>
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="p-6 text-center text-muted-foreground">Loading contacts...</div>
              ) : profiles.length === 0 ? (
                <div className="p-10 text-center">
                  <ContactRound className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No contacts captured yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Contact captures from widget conversations will appear here.</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {profiles.map((profile) => (
                    <button
                      key={profile.id}
                      onClick={() => setSelectedId(profile.id)}
                      className={`w-full p-4 text-left transition-colors hover:bg-secondary ${selectedId === profile.id ? 'bg-secondary' : ''}`}
                      data-testid={`contact-profile-${profile.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-foreground">{profile.name || profile.email || profile.phone || `Visitor ${profile.visitor_id?.slice(0, 8) || 'Unknown'}`}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {[profile.company, profile.source || profile.channel].filter(Boolean).join(' • ') || 'Captured lead'}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(profile.updated_at).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {profile.email && <span className="px-2 py-1 rounded-full bg-secondary">{profile.email}</span>}
                        {profile.phone && <span className="px-2 py-1 rounded-full bg-secondary">{profile.phone}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          <div className="apollo-card min-h-0 flex flex-col">
            {!selectedId ? (
              <div className="flex-1 flex items-center justify-center text-center p-10">
                <div>
                  <ContactRound className="w-14 h-14 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Select a contact to view details</p>
                </div>
              </div>
            ) : detailLoading || !selectedProfile ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">Loading contact details...</div>
            ) : (
              <>
                <div className="p-6 border-b border-border flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-semibold font-['Outfit']">{selectedProfile.name || 'Unnamed contact'}</h2>
                    <p className="text-muted-foreground mt-1">
                      {[selectedProfile.company, selectedProfile.source || selectedProfile.channel, selectedProfile.conversation_status].filter(Boolean).join(' • ')}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <p>Updated</p>
                    <p>{formatDate(selectedProfile.updated_at)}</p>
                  </div>
                </div>

                <ScrollArea className="flex-1">
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {fieldRows.map(({ key, label, icon: Icon }) => (
                        <div key={key} className="rounded-xl border border-border bg-card/60 p-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                          </div>
                          <p className="font-medium break-words">{selectedProfile[key] || '—'}</p>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-xl border border-border bg-card/60 p-5 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Sparkles className="w-4 h-4" />
                        <span>Conversation brief</span>
                      </div>
                      <p className="text-sm leading-6">{selectedProfile.conversation_brief?.summary || 'No linked conversation details yet.'}</p>
                      {selectedProfile.conversation_brief?.highlights?.length > 0 && (
                        <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
                          {selectedProfile.conversation_brief.highlights.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="rounded-xl border border-border bg-card/60 p-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Link2 className="w-4 h-4" />
                          <span>Source & context</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Source:</span> {selectedProfile.source || selectedProfile.channel || '—'}</p>
                          <p><span className="text-muted-foreground">Context:</span> {selectedProfile.context || '—'}</p>
                          <p><span className="text-muted-foreground">Visitor ID:</span> {selectedProfile.visitor_id || '—'}</p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-border bg-card/60 p-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Bot className="w-4 h-4" />
                          <span>Conversation</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Agent:</span> {selectedProfile.agent_name || '—'}</p>
                          <p><span className="text-muted-foreground">Channel:</span> {selectedProfile.channel || '—'}</p>
                          <p><span className="text-muted-foreground">Status:</span> {selectedProfile.conversation_status || '—'}</p>
                          <p><span className="text-muted-foreground">Captured:</span> {formatDate(selectedProfile.created_at)}</p>
                        </div>
                      </div>
                      <div className="rounded-xl border border-border bg-card/60 p-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <MessageSquareText className="w-4 h-4" />
                          <span>Message activity</span>
                        </div>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-muted-foreground">Messages:</span> {selectedProfile.conversation_stats?.message_count ?? 0}</p>
                          <p><span className="text-muted-foreground">Latest:</span> {formatDate(selectedProfile.conversation_stats?.last_message_at)}</p>
                          <p><span className="text-muted-foreground">Linked conversation:</span> {selectedProfile.conversation_id || '—'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border bg-card/60 p-4">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div>
                          <h3 className="font-medium">Linked conversation history</h3>
                          <p className="text-sm text-muted-foreground mt-1">Most recent linked conversation messages for this contact.</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{selectedProfile.messages?.length || 0} shown</span>
                      </div>

                      {!selectedProfile.messages?.length ? (
                        <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                          No linked messages found for this contact yet.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {selectedProfile.messages.map((message) => {
                            const meta = roleMeta[message.role] || roleMeta.system;
                            const Icon = meta.icon;

                            return (
                              <div key={message.id} className={`flex ${meta.wrapper}`}>
                                <div className="max-w-[85%] space-y-1">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Icon className="w-3.5 h-3.5" />
                                    <span>{meta.label}</span>
                                    <span>•</span>
                                    <span>{formatDate(message.created_at)}</span>
                                  </div>
                                  <div className={`rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap break-words ${meta.bubble}`}>
                                    {message.content}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {selectedProfile.conversation_metadata && Object.keys(selectedProfile.conversation_metadata).length > 0 && (
                      <div className="rounded-xl border border-border bg-card/60 p-4">
                        <h3 className="font-medium mb-3">Captured metadata</h3>
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                          {JSON.stringify(selectedProfile.conversation_metadata, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactsPage;
